import React from "react";
import { Fragment } from "react";

import clsx from "clsx";
import { FieldValues, Path, PathValue, RegisterOptions, UseFormRegister, UseFormSetValue, UseFormWatch } from "react-hook-form";

import { Listbox, Transition } from "@headlessui/react";
import { CheckIcon, SelectorIcon } from "@heroicons/react/outline";

import { SelectOption } from "./StyledSelectInput";
import classNames from './../../../utils/classNames';


interface StyledMultiSelectInputProps<T extends FieldValues> extends Omit<React.HTMLProps<HTMLInputElement>, 'onChange'> {
  register: UseFormRegister<T>;
  registerName: Path<T>;
  setValue: UseFormSetValue<T>;
  watch: UseFormWatch<T>;
  options: SelectOption[];
  onChange?: (value: string[]) => void;
  label?: string;
  registerOptions?: RegisterOptions<T, Path<T>>;
  noneSelectedText?: string;
  classes?: string;
  labelClasses?: string; // Tailwind CSS margin classes
}

export function StyledMultiSelectInput<T extends FieldValues>({
  register,
  registerName,
  setValue,
  watch,
  label,
  options,
  onChange,
  registerOptions,
  noneSelectedText = "None selected",
  classes,
  labelClasses
}: StyledMultiSelectInputProps<T>) {
  const selectValue = watch(registerName) || [] as string[];

  return (
    <div className={classes}>
      <Listbox
        {...register(registerName, registerOptions)}
        value={selectValue}
        onChange={(value) => {
          setValue(registerName, value as PathValue<T, Path<T>>);
          onChange && onChange(value as string[]);
        }}
        multiple
      >
        {({ open }) => (
          <>
            {label && (
              <Listbox.Label className={clsx("block mb-1 text-sm font-medium text-gray-700", labelClasses)}>
                {label}
              </Listbox.Label>
            )}
            <div className="w-full relative">
              <Listbox.Button
                className="mt-1 bg-white relative w-full border border-gray-300 rounded-md shadow-sm pl-3 pr-10 py-2 text-left cursor-default focus:outline-none focus:ring-1 focus:ring-etpink-500 focus:border-etpink-500 sm:text-sm">
                <span className="block truncate">
                  {Array.isArray(selectValue) && selectValue.length > 0
                    ? (
                      selectValue.length < 3
                        ? selectValue.map((val) => options.find((e) => e.value === val)?.label).join(', ')
                        : selectValue.length + " selected"
                    ) : noneSelectedText}
                </span>
                <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <SelectorIcon
                    className="h-5 w-5 text-gray-700"
                    aria-hidden="true"
                  />
                </span>
              </Listbox.Button>

              <Transition
                show={open}
                appear={true}
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Listbox.Options
                  className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                  {options.map((e) => (
                    <Listbox.Option
                      key={e.value}
                      className={({ active }) =>
                        classNames(
                          active
                            ? "text-white bg-etpink-600"
                            : "text-gray-900",
                          "cursor-default select-none relative py-2 pl-8 pr-4"
                        )
                      }
                      value={e.value}
                    >
                      <>
                        <span
                          className={classNames(
                            selectValue.includes(e.value) ? "font-semibold" : "font-normal",
                            "block truncate"
                          )}
                        >
                          {e.label}
                        </span>
                        {selectValue.includes(e.value) && (
                          <span
                            className="absolute inset-y-0 left-0 flex items-center pl-1.5 text-etpink-600"
                          >
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        )}
                      </>
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </Transition>
            </div>
          </>
        )}
      </Listbox>
    </div>
  );
}